import { useEffect, useState } from 'react';
import { getGameLeaderboard } from 'app/api/game';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const PAGE_SIZE = 20;

const useLeaderboard = (gameId) => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search input so we only fire one request after the admin
  // stops typing, instead of a request per keystroke.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // A new search always starts back at page 1 -- matches do not all come
  // back on the page the admin happened to be viewing.
  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (gameId) {
      fetchLeaderboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, pageNumber, debouncedSearch]);

  const fetchLeaderboard = async () => {
    setPageLoading(true);
    try {
      const response = await getGameLeaderboard(gameId, {
        pageNumber,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
      });
      if (response?.responseCode === 200) {
        setRows(response?.data?.rows || []);
        setTotalCount(response?.data?.totalCount ?? 0);
      } else {
        Toast.error(
          response?.response?.message || Message.Error.COMMON_MESSAGE
        );
      }
    } catch (error) {
      Toast.error(Message.Error.COMMON_MESSAGE);
    } finally {
      setPageLoading(false);
    }
  };

  const goToPage = (n) => {
    setPageNumber(Math.max(1, n));
  };

  return {
    rows,
    totalCount,
    pageNumber,
    pageSize: PAGE_SIZE,
    pageLoading,
    searchTerm,
    setSearchTerm,
    goToPage,
  };
};

export default useLeaderboard;
